import { useState } from "react";
import Avtar from "../images/profile.svg";
import { IoMdEye, IoMdEyeOff } from "react-icons/io";
import {
  IoCheckmarkCircle,
  IoCloseCircle,
  IoShieldCheckmark,
  IoWarningOutline,
} from "react-icons/io5";

interface DoctorDetail {
  doctorName: string;
  gender: string;
  phoneNo: string;
  emailId: string;
  qualification: string;
  languages: string;
  registrationNo: string;
  departmentId: number;
  departmentName: string;
  specializationId: number;
  specializationName: string;
  experienceYears: number;
  doctorFees: number;
  locationId: number;
  doctorImage?: string;
  location: string;
}

interface DoctorProfileProps {
  doctorDetail: DoctorDetail;
  onClose: () => void;
}

function DoctorProfile({ doctorDetail, onClose }: DoctorProfileProps) {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validatePassword = (password: string) => {
    return {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\|;'~/`]/.test(password),
      noUserInfo:
        !password.toLowerCase().includes(doctorDetail.doctorName.toLowerCase()) &&
        !password.toLowerCase().includes(doctorDetail.emailId.toLowerCase()),
    };
  };

  const getPasswordStrength = (password: string) => {
    if (!password) return { strength: "none", color: "gray", percentage: 0 };

    const policies = validatePassword(password);
    const passedCount = Object.values(policies).filter(Boolean).length;
    const totalPolicies = Object.keys(policies).length;

    const percentage = Math.round((passedCount / totalPolicies) * 100);

    if (percentage < 40) return { strength: "weak", color: "red", percentage };
    if (percentage < 65) return { strength: "medium", color: "yellow", percentage };
    if (percentage < 85) return { strength: "strong", color: "green", percentage };
    return { strength: "very-strong", color: "green", percentage };
  };

  const passwordPolicies = validatePassword(newPassword);
  const passwordStrength = getPasswordStrength(newPassword);
  const isPasswordValid = Object.values(passwordPolicies).every(Boolean);
  const passwordsMatch = newPassword === confirmPassword && newPassword.length > 0;

  const handleSave = () => {
    if (!isPasswordValid) {
      setError("Password does not meet all security requirements.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
  
    setError("");
    setNewPassword("");
    setConfirmPassword("");
    onClose();
  };

  const PolicyItem = ({
    isValid,
    text,
  }: {
    isValid: boolean;
    text: string;
  }) => (
    <li
      className={`flex items-center gap-2 transition-colors ${
        isValid ? "text-green-600" : "text-gray-500"
      }`}
    >
      {isValid ? (
        <IoCheckmarkCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
      ) : (
        <IoCloseCircle className="w-3 h-3 text-gray-400 flex-shrink-0" />
      )}
      <span className="text-xs">{text}</span>
    </li>
  );

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-[90%] max-w-xl relative max-h-[80vh] overflow-y-auto p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-500 hover:text-gray-800 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <h2 className="text-xl font-bold text-center text-gray-800 mb-3">
          Password Update
        </h2>

        <div className="flex items-center gap-3 mb-3">
          <img
                    src={`${process.env.REACT_APP_BASE_URL}/${doctorDetail?.doctorImage}`}
            alt={Avtar}
            className="w-14 h-16 rounded-full border "
          />
          <div>
            <h4 className="font-semibold text-gray-900 text-base">
              {doctorDetail.doctorName}
            </h4>
            <p className="text-sm text-gray-500">
              {doctorDetail.specializationName} | {doctorDetail.departmentName}
            </p>
            <p className="text-sm text-gray-500">{doctorDetail.emailId}</p>
          </div>
        </div>

        {/* Password form */}
        <div className="border-t pt-3">
          <h3 className="text-md font-semibold text-gray-800 mb-2">
            Change Password
          </h3>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex items-center gap-2 p-2 mb-2">
              <IoWarningOutline className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <div className="space-y-3">
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors p-2"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                  tabIndex={-1}
                >
                  {showNewPassword ? (
                    <IoMdEyeOff className="w-5 h-5" />
                  ) : (
                    <IoMdEye className="w-5 h-5" />
                  )}
                </button>
              </div>

              {newPassword && (
                <div className="mt-2">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-gray-600">Password Strength</span>
                    <span
                      className={`font-medium capitalize ${
                        passwordStrength.color === "red"
                          ? "text-red-600"
                          : passwordStrength.color === "yellow"
                          ? "text-yellow-600"
                          : passwordStrength.color === "green"
                          ? "text-green-600"
                          : "text-gray-600"
                      }`}
                    >
                      {passwordStrength.strength.replace("-", " ")} (
                      {passwordStrength.percentage}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${
                        passwordStrength.color === "red"
                          ? "bg-red-500"
                          : passwordStrength.color === "yellow"
                          ? "bg-yellow-500"
                          : passwordStrength.color === "green"
                          ? "bg-green-500"
                          : "bg-gray-400"
                      }`}
                      style={{ width: `${passwordStrength.percentage}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>

            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors p-2"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                  tabIndex={-1}
                >
                  {showConfirmPassword ? (
                    <IoMdEyeOff className="w-5 h-5" />
                  ) : (
                    <IoMdEye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-2">
              <div className="flex items-center gap-2 mb-1">
                <IoShieldCheckmark
                  className={`w-4 h-4 ${
                    isPasswordValid ? "text-green-500" : "text-gray-400"
                  }`}
                />
                <p className="text-xs font-medium text-gray-700">
                  Security Requirements
                </p>
              </div>

              <div className="mb-1">
                <p className="text-xs font-medium text-gray-600">
                  Basic Requirements:
                </p>
                <ul className="space-y-1">
                  <PolicyItem
                    isValid={passwordPolicies.minLength}
                    text="At least 8 characters"
                  />
                  <PolicyItem
                    isValid={passwordPolicies.hasUppercase}
                    text="One uppercase letter (A-Z)"
                  />
                  <PolicyItem
                    isValid={passwordPolicies.hasLowercase}
                    text="One lowercase letter (a-z)"
                  />
                  <PolicyItem
                    isValid={passwordPolicies.hasNumber}
                    text="One number (0-9)"
                  />
                  <PolicyItem
                    isValid={passwordPolicies.hasSpecialChar}
                    text="One special character (!@#$%^&*)"
                  />
                </ul>
              </div>

              <div>
                <p className="text-xs font-medium text-gray-600">Confirmation:</p>
                <ul className="space-y-1">
                  <PolicyItem isValid={passwordsMatch} text="Passwords match" />
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-3">
          <button
            onClick={onClose}
            className="border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors px-3 py-1"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!isPasswordValid || !passwordsMatch}
            className={`rounded-lg transition-colors flex items-center gap-2 px-3 py-1 ${
              isPasswordValid && passwordsMatch
                ? "bg-blue-600 text-white hover:bg-blue-700"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          >
            <IoShieldCheckmark className="w-4 h-4" />
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

export default DoctorProfile;
